import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { batch } from 'react-redux';
import {
  clearIncidentsAction,
  loadIncidentsAction,
  setIncidentsKeywordAction,
} from '../../../../redux/actions/incidents';
import { setDictionaryListAction } from '../../../../redux/slices/dictionary';
import GET_CALENDAR_INCIDENTS from '../../apollo/GET_CALENDAR_INCIDENTS';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import * as IncidentsSearchUI from './IncidentsSearchUI';
import { DictionaryTypeEnum } from '../../../../utils/interfaces';
import { assertNotNil } from '../../../../utils/types';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import IncidentTypeMenu from '../../../../components/Incidents/IncidentTypeMenu';
import { ALL_INCIDENTS_OPTION_VALUE } from '../../../../components/IncidentType';
import DatePickerWidget from '../../../../components/Builder/DatePickerWidget';
import { IDateInput } from '../../../../redux/slices/oldBuilder/date/types';
import {
  ICalendarIncidentsQuery,
  ICalendarIncidentsQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { INTERNAL_DATE_FORMAT } from '../../../../config/constants';
import toastMessage from '../../../../utils/toastMessage';
import { useApolloClient } from '@apollo/client';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { useAppDispatch } from '../../../../redux/hooks';

export default memo(function IncidentLocator() {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  const defaultConfig = useBootstrapSelector('defaultConfig');
  assertNotNil(defaultConfig);
  const { defaultEnd } = defaultConfig;

  // 3 months before defaultEnd
  const defaultStart = useMemo(
    () => moment(defaultEnd).subtract(3, 'months').format(INTERNAL_DATE_FORMAT),
    [defaultEnd],
  );

  const [dateInput, setDateInput] = useState<IDateInput>({
    startDate: defaultStart,
    endDate: defaultEnd,
    datePicker: {
      __typename: 'DatePickerFixed',
      start: defaultStart,
      end: defaultEnd,
    },
  });

  const handleDateChange = (newDateInput: IDateInput) => {
    setDateInput(newDateInput);
  };

  const handleOpenCreateModal = () => {
    dispatch(openModal({ type: ModalSliceType.IncidentCreate }));
  };

  /* Prepare incident types for search query */
  const [selectedIncidentType, setSelectedIncidentType] = useState(
    ALL_INCIDENTS_OPTION_VALUE,
  );

  /* Prepare keyword for search query */
  const [keyword, setKeyword] = useState('');

  const [isSearchLoading, setSearchLoading] = useState(false);
  /* Execute search query */
  const onSearchConfirm = useCallback(
    async (userExecuted = false) => {
      batch(() => {
        dispatch(clearIncidentsAction());
        dispatch(setIncidentsKeywordAction(keyword));
      });

      setSearchLoading(true);
      const { data } = await client.query<
        ICalendarIncidentsQuery,
        ICalendarIncidentsQueryVariables
      >({
        query: GET_CALENDAR_INCIDENTS,
        variables: {
          start: dateInput.startDate,
          end: dateInput.endDate,
          typeIds:
            selectedIncidentType === ALL_INCIDENTS_OPTION_VALUE
              ? []
              : [selectedIncidentType],
          keyword,
        },
      });

      batch(() => {
        dispatch(loadIncidentsAction({ incidents: data.incidents }));
        dispatch(
          setDictionaryListAction({
            newValues: data.incidents,
            dictionaryType: DictionaryTypeEnum.CalendarIncident,
          }),
        );
      });

      if ((data?.incidents?.length ?? 0) === 0 && userExecuted) {
        toastMessage({
          message: 'No results found.  Try expanding the date range.',
          type: 'error',
        });
      }
      setSearchLoading(false);
    },
    [client, dispatch, dateInput, selectedIncidentType, keyword],
  );

  const onKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    if (event.code === 'Enter') {
      onSearchConfirm(true);
      target.blur();
    }
  };

  /* Search the incidents from last 90 days upon mount */
  useEffect(() => {
    onSearchConfirm();
    // eslint-disable-next-line
  }, []);
  return (
    <IncidentsSearchUI.Container>
      <IncidentsSearchUI.Content>
        <IncidentsSearchUI.FieldsWrapper>
          <IncidentsSearchUI.Column>
            <IncidentsSearchUI.StyledLabel>
              Incident Type
            </IncidentsSearchUI.StyledLabel>
            <IncidentTypeMenu
              includeAllIncidents
              value={selectedIncidentType}
              onChange={setSelectedIncidentType}
            />
          </IncidentsSearchUI.Column>
          <IncidentsSearchUI.Column>
            <IncidentsSearchUI.StyledLabel>
              Keyword
            </IncidentsSearchUI.StyledLabel>
            <IncidentsSearchUI.StyledSearchInput
              value={keyword}
              onChange={setKeyword}
              onKeyDown={onKeywordKeyDown}
              placeholder={'Search Keyword'}
            />
          </IncidentsSearchUI.Column>
        </IncidentsSearchUI.FieldsWrapper>
        <SecondaryButton
          type="button"
          onClick={handleOpenCreateModal}
          isOutlined
          trackContext="incidents"
          trackAction="create incident"
        >
          + Create Incident
        </SecondaryButton>
      </IncidentsSearchUI.Content>
      <IncidentsSearchUI.Footer>
        <DatePickerWidget
          endDate={defaultEnd}
          onChange={handleDateChange}
          dateInput={dateInput}
          schemaId={null}
        />
        <PrimaryButton
          type="button"
          onClick={onSearchConfirm}
          loading={isSearchLoading}
          trackContext="incidents"
          trackAction="search"
        >
          Search
        </PrimaryButton>
      </IncidentsSearchUI.Footer>
    </IncidentsSearchUI.Container>
  );
});
